<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import alertImage from 'dashboard/assets/images/sales-registration-alert.svg';

const props = defineProps({
  show: { type: Boolean, default: false },
  status: {
    type: String,
    default: 'sending',
    validator: value => ['sending', 'success', 'error'].includes(value),
  },
});
const emit = defineEmits(['close', 'retry']);
const { t } = useI18n();
const canClose = computed(() => props.status === 'error');
const title = computed(
  () =>
    ({
      sending: t('SALES.DELIVERY.SUBMISSION.SENDING'),
      success: t('SALES.DELIVERY.SUBMISSION.SUCCESS'),
      error: t('SALES.DELIVERY.SUBMISSION.ERROR'),
    })[props.status]
);
const description = computed(() =>
  props.status === 'success'
    ? t('SALES.DELIVERY.SUBMISSION.SUCCESS_DESCRIPTION')
    : t('SALES.DELIVERY.SUBMISSION.ERROR_DESCRIPTION')
);
const close = () => {
  if (canClose.value) emit('close');
};
</script>

<template>
  <LaisModal
    :show="show"
    disable-escape-key-down
    max-width-class="max-w-[806px]"
    @close="close"
  >
    <div
      data-testid="sales-delivery-submission-dialog"
      class="flex min-h-[min(650px,calc(100dvh-4rem))] flex-col items-center justify-center px-4 py-8 text-center sm:px-10"
    >
      <span
        v-if="status === 'sending'"
        class="i-lucide-loader-circle size-16 animate-spin text-lais-primary"
        aria-hidden="true"
      />
      <span
        v-else-if="status === 'success'"
        class="i-lucide-circle-check size-20 text-lais-success"
        aria-hidden="true"
      />
      <img
        v-else
        :src="alertImage"
        :alt="t('SALES.DELIVERY.SUBMISSION.ERROR_IMAGE_ALT')"
        class="h-[219px] w-[202px] object-contain"
        data-testid="sales-delivery-submission-error-image"
      />
      <p
        class="mt-16 max-w-[640px] text-center text-[32px] font-bold leading-9 text-[#CC5A00]"
      >
        {{ title }}
      </p>
      <p
        v-if="status !== 'sending'"
        class="mt-6 max-w-[640px] text-center text-xl leading-6 text-[#464F53]"
      >
        {{ description }}
      </p>
      <div
        v-if="status === 'error'"
        class="mt-10 flex flex-wrap justify-center gap-3"
      >
        <LaisPrimaryButton icon="i-lucide-refresh-cw" @click="emit('retry')">
          {{ t('SALES.DELIVERY.SUBMISSION.RETRY') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('SALES.DELIVERY.SUBMISSION.BACK') }}
        </LaisPrimaryButton>
      </div>
    </div>
  </LaisModal>
</template>
